import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Observable, of, startWith, switchMap } from 'rxjs';
import { HomeService } from '../../home/home.service';

export interface BreadcrumbItem {
  label: string;
  url: string;
  iconDataUrl?: string;
}

@Component({
  selector: 'cmi-breadcrumb',
  imports: [AsyncPipe, RouterModule],
  templateUrl: './breadcrumb.html'
})
export class Breadcrumb {
  private readonly _router = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _homeService = inject(HomeService);

  public readonly breadcrumbs$: Observable<BreadcrumbItem[]> = this._router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null as NavigationEnd | null),
    switchMap((): Observable<BreadcrumbItem[]> => this._buildBreadcrumbs(this._activatedRoute.root))
  );

  private _buildBreadcrumbs(
    route: ActivatedRoute,
    url = '',
    breadcrumbs: BreadcrumbItem[] = []
  ): Observable<BreadcrumbItem[]> {
    // Get the child routes
    const children: ActivatedRoute[] = route.children;

    // Return if there are no more children
    if (children.length === 0) {
      return of(breadcrumbs);
    }

    // Iterate over each child
    for (const child of children) {
      // Get the route's URL segment
      const routeUrl = child.snapshot.url.map((segment) => segment.path).join('/');

      // Append route URL to URL
      if (routeUrl) {
        url += `/${routeUrl}`;
      }

      // Get parameters from the route
      const tag = child.snapshot.paramMap.get('tag');
      const key = child.snapshot.paramMap.get('key');

      // Add breadcrumb for tag - fetch the name from AppDetails
      if (tag && !breadcrumbs.some((b) => b.url === `/apps/${tag}`)) {
        const appDetail = this._homeService.getAppDetail(tag);
        breadcrumbs.push({
          label: appDetail?.title ?? tag,
          url: `/apps/${tag}`,
          iconDataUrl: appDetail?.iconDataUrl
        });

        // Add breadcrumb for key if exists
        if (key) {
          breadcrumbs.push({
            label: key,
            url: `/apps/${tag}/providers/${key}`
          });
        }

        // Recursive call for child routes
        return this._buildBreadcrumbs(child, url, breadcrumbs);
      }

      // Add breadcrumb for key if tag already exists
      if (key && tag) {
        breadcrumbs.push({
          label: key,
          url: `/apps/${tag}/providers/${key}`
        });
      }

      // Recursive call for child routes
      return this._buildBreadcrumbs(child, url, breadcrumbs);
    }

    return of(breadcrumbs);
  }
}
